// src/components/video/VideoTemplatesPage.tsx
// Track E7 — CRUD of video templates (Remotion composition IDs +
// fal.ai endpoints).

import { useCallback, useEffect, useState } from 'react';
import {
  Edit3, Eye, EyeOff, Film, Loader2, Plus, RefreshCw, Sparkles,
  Trash2,
} from 'lucide-react';

import {
  ApiError,
  createTemplate, deleteTemplate, listTemplates, updateTemplate,
  type VideoTemplate, type VideoTemplateIn, type VideoProvider,
} from '@/lib/api_video';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props { addNotification?: Notifier }

export function VideoTemplatesPage({ addNotification }: Props) {
  const [rows, setRows] = useState<VideoTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<VideoTemplate | null>(null);
  const [busy, setBusy] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listTemplates({ limit: 100 });
      setRows(out.items);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se cargaron templates',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (tpl: VideoTemplate) => {
    setBusy((b) => ({ ...b, [tpl.id]: 'toggle' }));
    try {
      const updated = await updateTemplate(tpl.id, { is_active: !tpl.is_active });
      setRows((r) => r.map((x) => (x.id === tpl.id ? updated : x)));
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'Error',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[tpl.id]; return n; });
    }
  };

  const handleDelete = async (tpl: VideoTemplate) => {
    if (!window.confirm(`¿Eliminar template "${tpl.name}"?`)) return;
    setBusy((b) => ({ ...b, [tpl.id]: 'delete' }));
    try {
      await deleteTemplate(tpl.id);
      addNotification?.({ type: 'success', title: 'Template eliminado' });
      await load();
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo eliminar',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[tpl.id]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Film className="text-emerald-400" /> Templates de video
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Composiciones Remotion + endpoints fal.ai + uploads manuales.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="o-btn o-btn-ghost flex items-center gap-2">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setShowCreate(true)} className="o-btn o-btn-primary flex items-center gap-2">
            <Plus size={16} /> Nuevo template
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-emerald-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Film size={40} className="mx-auto mb-3 opacity-50" />
          <p>Sin templates todavía.</p>
        </div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 uppercase text-xs">
              <tr>
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2">Provider</th>
                <th className="text-left px-4 py-2">Identificador</th>
                <th className="text-left px-4 py-2">Resolución</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {rows.map((tpl) => {
                const op = busy[tpl.id];
                const id = tpl.provider === 'remotion'
                  ? tpl.composition_id
                  : tpl.provider === 'fal_ai'
                  ? tpl.fal_endpoint
                  : '—';
                const ProviderIcon = tpl.provider === 'fal_ai' ? Sparkles : Film;
                return (
                  <tr key={tpl.id} className={`hover:bg-white/[0.03] ${!tpl.is_active ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-white">{tpl.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{tpl.slug}</div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 text-gray-300">
                        <ProviderIcon size={12} /> {tpl.provider}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-300 font-mono truncate max-w-xs">
                      {id}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {tpl.width && tpl.height ? `${tpl.width}×${tpl.height}` : '—'}
                      {tpl.fps && ` @ ${tpl.fps}fps`}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        tpl.is_active
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-zinc-500/20 text-zinc-300'
                      }`}>
                        {tpl.is_active ? 'Activo' : 'Desactivado'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleActive(tpl)}
                          disabled={!!op}
                          className="o-btn o-btn-xs o-btn-ghost"
                          title={tpl.is_active ? 'Desactivar' : 'Activar'}
                        >
                          {tpl.is_active ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                        <button
                          onClick={() => setEditing(tpl)}
                          disabled={!!op}
                          className="o-btn o-btn-xs o-btn-ghost"
                        >
                          <Edit3 size={12} />
                        </button>
                        <button
                          onClick={() => handleDelete(tpl)}
                          disabled={!!op}
                          className="o-btn o-btn-xs o-btn-ghost text-red-400"
                        >
                          {op === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(showCreate || editing) && (
        <TemplateDrawer
          initial={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={async () => { setShowCreate(false); setEditing(null); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

function TemplateDrawer({
  initial, onClose, onSaved, notify,
}: {
  initial: VideoTemplate | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState<VideoTemplateIn>({
    name: initial?.name || '',
    slug: initial?.slug || '',
    provider: initial?.provider || 'remotion',
    composition_id: initial?.composition_id || '',
    fal_endpoint: initial?.fal_endpoint || '',
    description: initial?.description || '',
    width: initial?.width || null,
    height: initial?.height || null,
    fps: initial?.fps || null,
    duration_seconds: initial?.duration_seconds || null,
    preview_bunny_url: initial?.preview_bunny_url || '',
    is_active: initial?.is_active ?? true,
    default_props: initial?.default_props || {},
  });
  const [defaultsJson, setDefaultsJson] = useState(
    JSON.stringify(initial?.default_props || {}, null, 2),
  );
  const [saving, setSaving] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    let parsed: Record<string, unknown> = {};
    try { parsed = JSON.parse(defaultsJson || '{}'); }
    catch {
      notify?.({ type: 'error', title: 'default_props JSON inválido' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, default_props: parsed };
      if (isEdit && initial) {
        await updateTemplate(initial.id, payload);
      } else {
        await createTemplate(payload);
      }
      notify?.({ type: 'success', title: isEdit ? 'Template actualizado' : 'Template creado' });
      await onSaved();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'Error',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-4">
            {isEdit ? 'Editar template' : 'Nuevo template'}
          </h3>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Slug</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({
                    ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                  }))}
                  disabled={isEdit}
                  required
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Provider</label>
              <div className="grid grid-cols-3 gap-2">
                {(['remotion', 'fal_ai', 'manual'] as VideoProvider[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, provider: p }))}
                    disabled={isEdit}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      form.provider === p
                        ? 'bg-white/10 border-white/20 text-white'
                        : 'bg-gray-900 border-gray-700 text-gray-400'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {form.provider === 'remotion' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Composition ID</label>
                <input
                  value={form.composition_id || ''}
                  onChange={(e) => setForm((f) => ({ ...f, composition_id: e.target.value }))}
                  placeholder="Intro"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
                  required
                />
              </div>
            )}
            {form.provider === 'fal_ai' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">fal.ai endpoint</label>
                <input
                  value={form.fal_endpoint || ''}
                  onChange={(e) => setForm((f) => ({ ...f, fal_endpoint: e.target.value }))}
                  placeholder="fal-ai/kling-video/v2/text-to-video"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Ancho</label>
                <input
                  type="number"
                  value={form.width || ''}
                  onChange={(e) => setForm((f) => ({ ...f, width: Number(e.target.value) || null }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Alto</label>
                <input
                  type="number"
                  value={form.height || ''}
                  onChange={(e) => setForm((f) => ({ ...f, height: Number(e.target.value) || null }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">FPS</label>
                <input
                  type="number"
                  value={form.fps || ''}
                  onChange={(e) => setForm((f) => ({ ...f, fps: Number(e.target.value) || null }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">default_props (JSON)</label>
              <textarea
                value={defaultsJson}
                onChange={(e) => setDefaultsJson(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white font-mono text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={!!form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-emerald-500 focus:ring-emerald-500"
              />
              <label htmlFor="active" className="text-sm text-gray-300">Activo</label>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-gray-700">
              <button type="button" onClick={onClose} className="o-btn o-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="o-btn o-btn-primary flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                {isEdit ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default VideoTemplatesPage;
